import React, { useMemo } from "react";
import { Icon } from "@iconify/react";

const TaskOccurrencesModal = ({
  show,
  taskOccurrences,
  workOrderStatuses,
  onClose,
  onWorkOrderClick,
  formatDate,
}) => {
  const occurrenceDisplayRows = useMemo(() => {
    const statusMap = new Map(
      (workOrderStatuses || []).map((status) => [String(status.id), status]),
    );

    const scheduledRows = Array.isArray(taskOccurrences?.[0])
      ? taskOccurrences[0].map((occurrence, index) => {
        const status =
          occurrence?.workOrderId === 0
            ? { colours: { bootstrapName: 'secondary' } }
            : occurrence?.workOrderStatusId
              ? statusMap.get(String(occurrence.workOrderStatusId))
              : null;
        return {
          key: `occ-${index}`,
          name: occurrence?.name,
          usage: occurrence?.usage != null ? parseInt(occurrence.usage, 10) : null,
          date: occurrence?.timestamp,
          workOrderNumber: occurrence?.workOrderNumber || "",
          workOrder: occurrence.workOrderId,
          statusId: occurrence?.workOrderId === 0 ? 0 : occurrence?.workOrderStatusId || null,
          status,
          statusColor: status?.colours?.bootstrapName || "secondary",
        };
      })
      : [];

    return [...scheduledRows];
  }, [taskOccurrences, workOrderStatuses]);

  if (!show) {
    return null;
  }

  const componentRisk = taskOccurrences[2]?.componentRisk || 0;
  const componentOpportunity = taskOccurrences[2]?.componentOpportunity || 0;
  const componentEndLifeRemain = taskOccurrences[2]?.componentEndLifeRemain || 0;
  const componentLifeRatio = parseInt(taskOccurrences[2]?.componentLifeRatio * 100, 10);
  const frequency = taskOccurrences[2]?.frequency;
  const meterUnits = taskOccurrences[2]?.assetMeter?.meter?.measurementUnits?.unitName?.toLowerCase() || "days";
  const forecastOccurrences = taskOccurrences[2]?.forecastOccurrences || 0;

  const riskOpp =
    componentRisk === 1
      ? "Risk"
      : componentOpportunity === 1
        ? "Opportunity"
        : "";

  const riskOppTolerance
    = forecastOccurrences > 0
      ? (frequency - componentEndLifeRemain) / forecastOccurrences
      : 0

  const riskOppDescription =
    componentRisk === 1
      ? `If future achieved component change frequency is ${parseInt(riskOppTolerance, 10)} ${meterUnits} (${componentLifeRatio}%) below the target, an additional component will need to be changed within the asset life, increasing lifecycle cost.`
      : componentOpportunity === 1
        ? `If you can achieve a future change frequency of ${parseInt(riskOppTolerance, 10)} ${meterUnits} (${100 - componentLifeRatio}%) above the target, one less component change will be needed within the asset life, reducing lifecycle cost.`
        : "";

  return (
    <div className="modal modal-backdrop show d-block" tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">
              {taskOccurrences?.[1]?.title
                ?.toLowerCase()
                .replace(/\b\w/g, char => char.toUpperCase())
                || 'Task Occurrences'}
            </h5>

            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            />
          </div>
          <div className="modal-body">
            {riskOpp && (<div className="col-md-12 ">
              <div
                className={`alert ${riskOpp === "Risk" ? "alert-danger" : "alert-success"
                  } py-12 d-flex align-items-start`}
                role="alert"
              >
                <Icon
                  icon={
                    riskOpp === "Risk"
                      ? "mdi:alert-circle-outline"
                      : "mdi:lightbulb-on-outline"
                  }
                  className="me-16 mt-1"
                  width="35"
                  height="35"
                />
                <span>{riskOppDescription}</span>
              </div>
            </div>
            )}
            <div className="occurrences-container mt-24">
              <table className="table bordered-table table-sm">
                <thead>
                  <tr>
                    <th>Task Name</th>
                    <th>Work Order #</th>
                    <th>Usage ({taskOccurrences?.[1]?.measurementUnits})</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {occurrenceDisplayRows.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center py-4 rounded-0">No Occurrences Forecast</td>
                    </tr>
                  )}
                  {occurrenceDisplayRows.map((row) => (
                    <tr key={row.key}>
                      <td>{row.name}</td>
                      <td>
                        {row.workOrderNumber ? (
                          <button
                            type="button"
                            className="btn btn-link p-0"
                            onClick={() => onWorkOrderClick(row.workOrder)}
                          >
                            {row.workOrderNumber}
                          </button>
                        ) : (
                          ""
                        )}
                      </td>
                      <td>{row.usage != null ? parseInt(row.usage, 10) : ""}</td>
                      <td>{formatDate(row.date)}</td>
                      <td>
                        {row.statusId !== null && row.status ? (
                          <div
                            className={`badge bg-${row.statusColor}-focus text-${row.statusColor}`}
                          >
                            {row.workOrder === 0 ? "Last Done" : row.status.name}
                          </div>
                        ) : (
                          ""
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskOccurrencesModal;
